import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, CalendarDays, Trash2, AlertCircle } from 'lucide-react';
import { Gate } from '../AccessStudio';
import ConfirmDialog from './ConfirmDialog';

const money = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0.00';
  return Number(value).toFixed(2);
};

const today = () => new Date().toISOString().split('T')[0];

/**
 * Log attendance days against a daily afterschool enrollment.
 *
 * A daily enrollment is a standing arrangement, not a bill — the student is
 * charged each time they actually attend, so one enrollment accumulates many
 * charges across the year. Each entry becomes a MonthlyCharges row, which is
 * what the family balance and the Payments module read.
 */
const AfterschoolDayChargesModal = ({
  enrollment, apiCall, onClose, onChanged, showSuccess, showError,
}) => {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const packageRate = enrollment.DailyPrice ?? enrollment.FinalCost ?? '';

  const [form, setForm] = useState({
    chargeDate: today(),
    days: '1',
    unitPrice: packageRate === null ? '' : String(packageRate),
    discountAmount: '',
    discountReason: '',
    notes: '',
  });

  const isActive = enrollment.Status === 'Active';

  const loadCharges = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/afterschool/enrollments/${enrollment.EnrollmentID}/day-charges`);
      setCharges(data);
    } catch (error) {
      showError('Error loading day charges: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, enrollment.EnrollmentID, showError]);

  useEffect(() => {
    loadCharges();
  }, [loadCharges]);

  const preview = useMemo(() => {
    const days = parseInt(form.days, 10) || 0;
    const rate = parseFloat(form.unitPrice);
    const discount = parseFloat(form.discountAmount) || 0;
    if (!days || isNaN(rate)) return null;
    const gross = Math.round(rate * days * 100) / 100;
    const final = Math.round((gross - discount) * 100) / 100;
    return { days, rate, gross, discount, final, invalid: discount < 0 || discount > gross };
  }, [form]);

  const totals = useMemo(() => {
    const live = charges.filter((c) => c.Status !== 'Cancelled');
    return {
      entries: live.length,
      charged: live.reduce((s, c) => s + (Number(c.FinalAmount) || 0), 0),
      paid: live.reduce((s, c) => s + (Number(c.AmountPaid) || 0), 0),
    };
  }, [charges]);

  const [dialog, setDialog] = useState(null);
  const closeDialog = () => setDialog(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!preview || preview.invalid) return;
    setDialog({
      kind: 'add',
      tone: 'info',
      title: 'Add day charge',
      message: `Charge ${enrollment.FirstName} ${enrollment.LastName} for ${preview.days} day${preview.days !== 1 ? 's' : ''}?`,
      details: `${preview.days} × $${money(preview.rate)}`
        + (preview.discount > 0 ? ` − $${money(preview.discount)} discount` : '')
        + ` = $${money(preview.final)}, posted to the family balance immediately.`,
      confirmLabel: 'Add charge',
    });
  };

  const doAdd = async () => {
    try {
      setSaving(true);
      const result = await apiCall(
        `/afterschool/enrollments/${enrollment.EnrollmentID}/day-charges`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chargeDate: form.chargeDate,
            days: parseInt(form.days, 10),
            unitPrice: form.unitPrice === '' ? null : parseFloat(form.unitPrice),
            discountAmount: parseFloat(form.discountAmount) || 0,
            discountReason: form.discountReason || null,
            notes: form.notes || null,
          }),
        }
      );
      showSuccess(result.message || 'Day charge added');
      setForm((prev) => ({
        ...prev,
        chargeDate: today(),
        days: '1',
        discountAmount: '',
        discountReason: '',
        notes: '',
      }));
      closeDialog();
      await loadCharges();
      if (onChanged) onChanged();
    } catch (error) {
      showError('Error adding day charge: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (charge) => {
    if (Number(charge.AmountPaid) > 0) {
      showError('This charge has already been paid against — refund or reallocate the payment first.');
      return;
    }
    setDialog({
      kind: 'delete',
      charge,
      tone: 'danger',
      title: 'Cancel day charge',
      message: `Remove this charge of $${money(charge.FinalAmount)}?`,
      details: charge.Description,
      confirmLabel: 'Cancel charge',
      cancelLabel: 'Keep it',
    });
  };

  const doDelete = async (charge) => {
    try {
      setSaving(true);
      await apiCall(`/afterschool/day-charges/${charge.ChargeID}`, { method: 'DELETE' });
      showSuccess('Day charge cancelled');
      closeDialog();
      await loadCharges();
      if (onChanged) onChanged();
    } catch (error) {
      showError('Error cancelling day charge: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const runDialog = () => {
    if (!dialog) return;
    if (dialog.kind === 'add') return doAdd();
    if (dialog.kind === 'delete') return doDelete(dialog.charge);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-amber-50 to-orange-50 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <CalendarDays className="w-6 h-6 text-amber-600" />
              Daily Charges
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              <strong>{enrollment.FirstName} {enrollment.LastName}</strong>
              {' · '}{enrollment.PackageName}
              {' · '}Family {enrollment.FamilyID}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 p-6 border-b bg-gray-50">
          <div>
            <div className="text-xs text-gray-600 uppercase font-semibold">Times Charged</div>
            <div className="text-2xl font-bold text-gray-900">{totals.entries}</div>
          </div>
          <div>
            <div className="text-xs text-gray-600 uppercase font-semibold">Total Charged</div>
            <div className="text-2xl font-bold text-blue-600">${money(totals.charged)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-600 uppercase font-semibold">Outstanding</div>
            <div className={`text-2xl font-bold ${
              totals.charged - totals.paid > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              ${money(totals.charged - totals.paid)}
            </div>
          </div>
        </div>

        {/* Add form */}
        {isActive ? (
          <Gate permission="afterschool.daily.manage" label="Add Day Charge">
            <form onSubmit={handleSubmit} className="p-6 border-b">
              <h3 className="font-semibold text-gray-800 mb-4">Log attendance</h3>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.chargeDate}
                    onChange={(e) => setForm({ ...form, chargeDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Days *</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.days}
                    onChange={(e) => setForm({ ...form, days: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Rate / day *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.unitPrice}
                    onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Discount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.discountAmount}
                    onChange={(e) => setForm({ ...form, discountAmount: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Discount reason</label>
                  <input
                    type="text"
                    value={form.discountReason}
                    onChange={(e) => setForm({ ...form, discountReason: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g. Sibling"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Note</label>
                  <input
                    type="text"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Optional"
                  />
                </div>
              </div>

              {preview && (
                <div className={`mt-4 p-4 rounded-lg border ${
                  preview.invalid ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                }`}>
                  {preview.invalid ? (
                    <p className="text-sm text-red-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Discount must be between $0 and ${money(preview.gross)}.
                    </p>
                  ) : (
                    <div className="text-sm text-amber-900">
                      {preview.days} day{preview.days !== 1 ? 's' : ''} × ${money(preview.rate)}
                      {' = '}<strong>${money(preview.gross)}</strong>
                      {preview.discount > 0 && <> − ${money(preview.discount)} discount</>}
                      {' → '}
                      <strong className="text-lg">${money(preview.final)}</strong> added to the family balance
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={saving || !preview || preview.invalid}
                  className="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-semibold"
                >
                  {saving ? 'Adding...' : 'Add Charge'}
                </button>
              </div>
            </form>
          </Gate>
        ) : (
          <div className="p-6 border-b bg-gray-50 text-sm text-gray-600">
            This enrollment is {enrollment.Status.toLowerCase()} — no new days can be charged.
            Existing charges are shown below.
          </div>
        )}

        {/* History */}
        <div className="p-6">
          <h3 className="font-semibold text-gray-800 mb-4">History</h3>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : charges.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No days charged yet for this enrollment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {charges.map((c) => {
                    const cancelled = c.Status === 'Cancelled';
                    return (
                      <tr key={c.ChargeID} className={cancelled ? 'opacity-50' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          {new Date(c.ChargeDate).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{c.Description}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-semibold">
                          ${money(c.FinalAmount)}
                          {Number(c.DiscountAmount) > 0 && (
                            <div className="text-xs text-green-600 font-normal">
                              -${money(c.DiscountAmount)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-sm text-green-700">
                          ${money(c.AmountPaid)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            c.Status === 'Paid' ? 'bg-green-100 text-green-800'
                              : c.Status === 'Partial' ? 'bg-yellow-100 text-yellow-800'
                              : cancelled ? 'bg-gray-100 text-gray-600'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {c.Status}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">
                          {!cancelled && Number(c.AmountPaid) === 0 && (
                            <Gate permission="afterschool.daily.manage" label="Cancel Day Charge">
                              <button
                                onClick={() => askDelete(c)}
                                className="text-red-600 hover:text-red-800"
                                title="Cancel this charge"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </Gate>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-semibold"
          >
            Close
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={!!dialog}
        tone={dialog?.tone}
        title={dialog?.title}
        message={dialog?.message}
        details={dialog?.details}
        confirmLabel={dialog?.confirmLabel}
        cancelLabel={dialog?.cancelLabel}
        busy={saving}
        onConfirm={runDialog}
        onCancel={closeDialog}
      />
    </div>
  );
};

export default AfterschoolDayChargesModal;
